import destinations from 'scripts/cache/destinations.json';
import { fetchBlogBySlug, fetchBlogsByCategory } from '../../src/entities/Blogs/service';
import { BlogsDetailPage } from '../../src/screens/BlogsDetailPage';
import { GetStaticProps } from 'next';
import { BlogDetailsProps } from '@/screens/BlogsDetailPage/ui/BlogsDetailPage';
import { getRedirectsPath } from '@/common/service/api/Redirects/getRedirectsPath';
import { fetchAuthorBySlug } from '@/entities/Blogs/service/fetchBlogBySlug';
import { getRecommends } from '@/shared/FeaturedActivities/service/ApiFeaturedActivities';
import { getPlaceholders } from '@/common/service/api/Activity/Activity';

type PageStaticProps = {
    blogSlug: string[];
};

export async function getStaticPaths() {
    return {
        paths: [],
        fallback: 'blocking',
    };
}

export const getStaticProps: GetStaticProps<BlogDetailsProps, PageStaticProps> = async ({
    params,
}) => {
    try {
        if (!params) {
            throw new Error(`BLOG DETAILS: BAD PARAMS: ${params}`);
        }
        const slug = params.blogSlug[0];
        const pageContent = await fetchBlogBySlug(slug);

        const currentPath = `/blog/${slug}/`;

        if (!pageContent) {
            const redirectPath = await getRedirectsPath(currentPath);

            if (redirectPath === '/404/') {
                return {
                    notFound: true,
                };
            }

            return {
                revalidate: Number(process.env.CACHE_ENV),
                redirect: {
                    destination: redirectPath.toLowerCase(),
                    statusCode: 301,
                },
            };
        }

        const rightPath = `/blog/${pageContent.slug}/`.toLowerCase();

        if (currentPath !== rightPath) {
            return {
                revalidate: Number(process.env.CACHE_ENV),
                redirect: {
                    destination: rightPath,
                    statusCode: 301,
                },
            };
        }

        const pageDestination =
            destinations.find((destination) => +destination.id === +pageContent.category.id) ||
            destinations[0];

        const { content } = pageContent;

        pageContent.content = content
            .replace(
                '<iframe',
                '<div id="text-replace" style="height: calc(100vw / 1.5); max-height: 460px; overflow: hidden"><iframe'
            )
            .replace('</iframe>', '</iframe></div>')
            .replace(/<img\s/g, '<img loading="lazy" width="350" height="230" ');

        const authorSlug = pageContent.author?.toLowerCase().replace(/\s/g, '-') || '';

        const [blogs, author, recommends] = await Promise.all([
            fetchBlogsByCategory({ category_id: pageDestination.id, limit: 6 }),
            fetchAuthorBySlug(authorSlug),
            getRecommends({
                destination_id: pageDestination.id,
            }),
        ]);

        const placeholders = await getPlaceholders(
            recommends.map(({ id }) => id),
            'activity'
        );

        recommends.forEach((i) => {
            i.placeholder = placeholders[i.id] || '';
        });

        const breadcrumbs = [
            {
                url: `/blog/`,
                title: 'Blogs',
            },
            {
                url: `/blog/${pageContent.slug}`.toLowerCase(),
                title: pageContent.title,
                unlink: true,
            },
        ];

        return {
            props: {
                pageContent,
                breadcrumbs,
                blogs,
                authorDescription: author?.description ? author.description : '',
                currentDestination: pageDestination,
                authorSlug,
                recommendedTrips: recommends,
                recommendedIds: recommends.map(({ id }) => id),
            },
            revalidate: 3600,
        };
    } catch (e) {
        throw new Error((e as Error).message);
    }
};

export default BlogsDetailPage;
